function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Container, CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters, Delta, Pending } from "../../components/analytics-kit";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import ServiceFactory from "../../service";
import { AuthBase } from "../../../plunk/service/auth-base";
import endpoints from "../../endpoints";
import account from "../../data/account";
import { num } from "../sales/territory-common";
import { Link } from "react-router-dom";
import PlunkDropDown from "../../components/plunk-dropdown";

/*
 * Activity Log — Settings → Activity Log (AUDIT_ACTIVITY_LOG.md §4 +
 * mockups/activity-log.html). Admin-gated (owner/administrator/root).
 *
 * Binds to EventService:
 *   GET /v1/event/activity          filters: actorType/actorId/resourceType/
 *                                   resourceId/category/severity/action/
 *                                   startDate/endDate + keyset (afterId —
 *                                   totalCount comes back -1, hasMore drives
 *                                   paging; stable under live writes)
 *   GET /v1/event/activity/export   CSV of the current filter — the export
 *                                   itself is audit-logged (audit.exported)
 *
 * The `action` filter accepts a comma-separated IN-list (Service.EventStore
 * 1.0.4) — multi-action saved views are ONE keyset query. Free-text search
 * filters the LOADED rows only — the backend has no text search; labeled
 * accordingly.
 */

const PAGE = 50;
const SAVED_VIEWS = [{
  key: "all",
  label: "All activity",
  actions: null
}, {
  key: "exports",
  label: "Data exports",
  actions: ["contact.exported", "analytics.exported", "audit.exported"]
}, {
  key: "signin",
  label: "Sign-in activity",
  actions: ["auth.login.succeeded", "auth.login.failed", "auth.logout", "auth.password.changed", "auth.password.reset_requested"]
}, {
  key: "campaigns",
  label: "Campaign changes",
  actions: ["campaign.created", "campaign.updated", "campaign.status_changed", "campaign.budget_changed", "automation.created", "automation.activated", "automation.paused"]
}, {
  key: "permissions",
  label: "Permission changes",
  actions: ["user.invited", "user.removed", "user.role_changed", "apikey.created", "apikey.rotated", "apikey.revoked", "tenant.config_changed"]
}];
const CATEGORIES = ["", "Auth", "Data", "Campaign", "Permissions", "Billing", "Agent", "Config"];
const ACTORS = ["", "User", "System", "Agent", "Automation"];
const OUTCOME_CLS = {
  success: "ok",
  failed: "bad",
  denied: "warn"
};
function isAdmin() {
  const groups = ((account.get() || {}).groups || []).map(g => String(g).toLowerCase());
  return ["owner", "administrator", "root"].some(g => groups.includes(g));
}
function fetchActivity(params) {
  const qs = Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
  const api = `/event/activity${qs ? `?${qs}` : ""}`;
  const Service = ServiceFactory.getType(api, "product");
  return new Service().getAsync();
}
function parseDetails(row) {
  try {
    return row.details ? JSON.parse(row.details) : null;
  } catch (e) {
    return null;
  }
}

// Rows read as sentences — humanize known actions; honest fallback for the rest.
function sentence(row) {
  const d = parseDetails(row) || {};
  const res = row.resourceName || row.resourceId || "";
  switch (row.action) {
    case "feed.run_completed":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Feed run completed \u2014 ", /*#__PURE__*/React.createElement("b", null, res || d.provider), ": ", num(d.rowsUpserted || d.rowsRead || 0), " rows", d.durationSeconds ? ` in ${Math.round(d.durationSeconds / 60)}m` : "");
    case "feed.run_failed":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Feed run FAILED \u2014 ", /*#__PURE__*/React.createElement("b", null, res || d.provider), d.message ? `: ${d.message}` : "");
    case "analytics.exported":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Exported ", /*#__PURE__*/React.createElement("b", null, d.rows !== undefined ? `${num(d.rows)} rows` : res || "analytics data"), d.format ? ` (${d.format})` : " (CSV)", d.range ? ` · ${d.range}` : "");
    case "contact.exported":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Exported ", /*#__PURE__*/React.createElement("b", null, d.count !== undefined ? `${num(d.count)} contacts` : "contacts"), d.format ? ` (${d.format})` : "", d.filter ? ` · filter: ${d.filter}` : "");
    case "audit.exported":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Exported the ", /*#__PURE__*/React.createElement("b", null, "activity log"), d.rows !== undefined ? ` (${num(d.rows)} rows)` : "");
    case "contact.consent_changed":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Changed consent for ", /*#__PURE__*/React.createElement("b", null, res), d.consentType ? ` · ${d.consentType}` : "");
    case "contact.unsubscribed":
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("b", null, res || "A contact"), " unsubscribed", d.source ? ` (${d.source})` : "");
    case "user.invited":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Invited ", /*#__PURE__*/React.createElement("b", null, res), d.role ? ` as ${d.role}` : "");
    case "user.removed":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Removed user ", /*#__PURE__*/React.createElement("b", null, res));
    case "user.role_changed":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Changed role for ", /*#__PURE__*/React.createElement("b", null, res), d.before && d.after ? `: ${d.before} → ${d.after}` : "");
    case "campaign.created":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Created campaign ", /*#__PURE__*/React.createElement("b", null, res));
    case "campaign.updated":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Updated campaign ", /*#__PURE__*/React.createElement("b", null, res));
    case "campaign.status_changed":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Campaign ", /*#__PURE__*/React.createElement("b", null, res), d.before && d.after ? `: ${d.before} → ${d.after}` : " status changed");
    case "campaign.budget_changed":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Changed budget on ", /*#__PURE__*/React.createElement("b", null, res), d.before !== undefined ? `: ${d.before} → ${d.after}` : "");
    case "automation.sent":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Automation ", /*#__PURE__*/React.createElement("b", null, res), " sent", d.recipient ? ` to ${d.recipient}` : "");
    case "audience.created":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Created audience ", /*#__PURE__*/React.createElement("b", null, res));
    case "audience.renamed":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Renamed audience ", /*#__PURE__*/React.createElement("b", null, res), d.before ? ` (was “${d.before}”)` : "");
    case "audience.deleted":
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Deleted audience ", /*#__PURE__*/React.createElement("b", null, res), d.membersRemoved !== undefined ? ` (${num(d.membersRemoved)} members unmapped)` : "");
    default:
      {
        const verb = row.action || "activity";
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("b", null, verb), res ? /*#__PURE__*/React.createElement(React.Fragment, null, " \u2014 ", res) : null);
      }
  }
}
function fmtWhen(iso) {
  const d = new Date(iso + (String(iso).endsWith("Z") ? "" : "Z"));
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  const label = sameDay ? "Today" : d.toDateString() === y.toDateString() ? "Yesterday" : d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
  return {
    label: `${label} ${d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit"
    })}`,
    utc: String(iso).slice(0, 19).replace("T", " ") + " UTC"
  };
}
class ActivityLogViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "loadMore", async () => {
      this.setState({
        loadingMore: true
      });
      try {
        const view = SAVED_VIEWS.find(v => v.key === this.state.view) || SAVED_VIEWS[0];
        const r = await this.fetchMerged(view, this.state.cursors);
        if (this._alive) this.setState(s => ({
          rows: s.rows.concat(r.rows.filter(x => !s.rows.some(y => y.id === x.id))),
          hasMore: r.hasMore,
          cursors: r.cursors,
          loadingMore: false
        }));
      } catch (e) {
        if (this._alive) this.setState({
          loadingMore: false
        });
      }
    });
    _defineProperty(this, "setView", key => this.setState({
      view: key
    }, () => this.load()));
    _defineProperty(this, "applyFilters", () => this.load());
    _defineProperty(this, "exportCsv", async () => {
      this.setState({
        exporting: true
      });
      try {
        const base = this.filters();
        const view = SAVED_VIEWS.find(v => v.key === this.state.view) || SAVED_VIEWS[0];
        if (view.actions) base.action = view.actions.join(",");
        const qs = Object.entries(base).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
        const url = `${endpoints.product.root}/event/activity/export${qs ? `?${qs}` : ""}`;
        const res = await fetch(url, {
          headers: {
            Authorization: "Bearer " + (AuthBase.idToken || ""),
            "x-app-id": (account.get() || {}).tenantId || ""
          }
        });
        if (!res.ok) throw new Error("HTTP " + res.status);
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `activity-log-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        const t = document.querySelector("paper-toast");
        if (t) t.show("Export downloaded — and logged to the activity log.");
      } catch (e) {
        const t = document.querySelector("paper-toast");
        if (t) t.show("Export failed: " + (e.message || "error"));
      }
      this.setState({
        exporting: false
      });
    });
    this.state = {
      rows: null,
      error: false,
      hasMore: false,
      loadingMore: false,
      view: "all",
      actorType: "",
      category: "",
      startDate: "",
      endDate: "",
      search: "",
      drawer: null,
      exporting: false,
      cursors: {} // per-action afterId cursors for multi-action views
    };
    this._alive = true;
  }
  componentDidMount() {
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  filters() {
    const {
      actorType,
      category,
      startDate,
      endDate
    } = this.state;
    const f = {};
    if (actorType) f.actorType = actorType;
    if (category) f.category = category;
    if (startDate) f.startDate = startDate;
    if (endDate) f.endDate = `${endDate}T23:59:59`;
    return f;
  }

  // Multi-action views send the action list as a comma-separated IN-list —
  // one keyset stream regardless of view.
  async fetchMerged(view, cursors) {
    const base = this.filters();
    if (view.actions) base.action = view.actions.join(",");
    const afterId = cursors.__all;
    const res = await fetchActivity({
      ...base,
      "$top": PAGE,
      afterId
    });
    const entries = res.entries || [];
    return {
      rows: entries,
      hasMore: !!res.hasMore,
      cursors: {
        __all: entries.length ? entries[entries.length - 1].id : afterId
      }
    };
  }
  async load() {
    this.setState({
      rows: null,
      error: false,
      cursors: {}
    });
    try {
      const view = SAVED_VIEWS.find(v => v.key === this.state.view) || SAVED_VIEWS[0];
      const r = await this.fetchMerged(view, {});
      if (this._alive) this.setState({
        rows: r.rows,
        hasMore: r.hasMore,
        cursors: r.cursors
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  render() {
    if (!isAdmin()) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "The Activity Log is available to workspace owners and administrators."));
    }
    const {
      rows,
      error,
      hasMore,
      loadingMore,
      view,
      actorType,
      category,
      startDate,
      endDate,
      search,
      drawer,
      exporting
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    const q = search.trim().toLowerCase();
    const shown = (rows || []).filter(r => !q || [r.actorName, r.actorId, r.action, r.resourceName, r.resourceId, r.ipAddress, r.details].some(x => String(x || "").toLowerCase().includes(q)));
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view activity-log-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: "/Settings"
      }, "Settings"), " \xB7 Activity Log"),
      title: "Activity Log",
      subtitle: "Who did what, when \xB7 records are append-only \xB7 retained 13 months \xB7 exports are logged",
      chips: [rows ? {
        k: "Loaded",
        v: num(rows.length)
      } : null],
      actions: /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: this.exportCsv,
        disabled: exporting
      }, exporting ? "Exporting…" : "Export CSV")
    }), /*#__PURE__*/React.createElement("nav", {
      className: "wa-switch",
      "aria-label": "Activity Log views"
    }, SAVED_VIEWS.map(v => /*#__PURE__*/React.createElement("a", {
      key: v.key,
      className: "wa-tab" + (view === v.key ? " on" : ""),
      style: {
        cursor: "pointer"
      },
      onClick: () => this.setView(v.key)
    }, v.label))), /*#__PURE__*/React.createElement(Card, {
      title: "Events",
      hint: rows ? `${num(shown.length)} of ${num(rows.length)} loaded · click a row for detail` : null
    }, /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar"
    }, /*#__PURE__*/React.createElement(Filters, null, /*#__PURE__*/React.createElement("input", {
      type: "date",
      className: "cust-search wa-search",
      style: {
        width: 150
      },
      value: startDate,
      onChange: e => this.setState({
        startDate: e.target.value
      }),
      title: "From date"
    }), /*#__PURE__*/React.createElement("input", {
      type: "date",
      className: "cust-search wa-search",
      style: {
        width: 150
      },
      value: endDate,
      onChange: e => this.setState({
        endDate: e.target.value
      }),
      title: "To date"
    }), /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "140px",
      value: actorType,
      onChange: v => this.setState({
        actorType: v
      }),
      options: ACTORS.map(a => [a, a || "Anyone"])
    }), /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "170px",
      value: category,
      onChange: v => this.setState({
        category: v
      }),
      options: CATEGORIES.map(c => [c, c || "All categories"])
    }), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      onClick: this.applyFilters
    }, "Apply"), /*#__PURE__*/React.createElement(Search, {
      value: search,
      onChange: v => this.setState({
        search: v
      }),
      placeholder: "Actor, resource, IP"
    }))), rows === null ? /*#__PURE__*/React.createElement(Pending, null) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "When"), /*#__PURE__*/React.createElement("th", null, "Actor"), /*#__PURE__*/React.createElement("th", null, "Activity"), /*#__PURE__*/React.createElement("th", null, "Outcome"), /*#__PURE__*/React.createElement("th", null, "IP"), /*#__PURE__*/React.createElement("th", null))), /*#__PURE__*/React.createElement("tbody", null, shown.map(r => {
      const w = fmtWhen(r.createdDate);
      const oc = String(r.outcome || "success").toLowerCase();
      return /*#__PURE__*/React.createElement("tr", {
        key: r.id,
        className: "clickable",
        onClick: () => this.setState({
          drawer: r
        })
      }, /*#__PURE__*/React.createElement("td", {
        title: w.utc,
        style: {
          whiteSpace: "nowrap"
        }
      }, w.label), /*#__PURE__*/React.createElement("td", {
        style: {
          whiteSpace: "nowrap"
        }
      }, /*#__PURE__*/React.createElement("span", {
        className: `al-actor ${String(r.actorType || "").toLowerCase()}`
      }, r.actorType || "?"), " ", r.actorName || r.actorId || "—"), /*#__PURE__*/React.createElement("td", null, sentence(r)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Pill, {
        tone: oc === "success" ? "success" : oc === "failure" || oc === "failed" || oc === "denied" ? "danger" : "neutral"
      }, oc), r.severity && r.severity !== "Info" ? /*#__PURE__*/React.createElement("span", {
        style: {
          marginLeft: 4
        }
      }, /*#__PURE__*/React.createElement(Pill, {
        tone: r.severity === "Critical" ? "danger" : "warning"
      }, r.severity)) : null), /*#__PURE__*/React.createElement("td", {
        className: "code",
        style: {
          display: "table-cell"
        }
      }, r.ipAddress || "—"), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons",
        style: {
          fontSize: "16px",
          color: "#999"
        }
      }, "chevron_right")));
    })))), shown.length === 0 && /*#__PURE__*/React.createElement(Empty, null, view === "signin" ? "No sign-in events yet. Sign-in auditing activates with the platform's Cognito auth triggers." : "No activity matches this view yet. Events appear here as your team and Plunk's automations work."), hasMore && /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar",
      style: {
        marginTop: 10
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      onClick: this.loadMore,
      disabled: loadingMore
    }, loadingMore ? "Loading…" : "Load more")))), drawer && /*#__PURE__*/React.createElement(DetailDrawer, {
      row: drawer,
      onClose: () => this.setState({
        drawer: null
      })
    }));
  }
}
function DetailDrawer({
  row,
  onClose
}) {
  const d = parseDetails(row);
  const w = fmtWhen(row.createdDate);
  const diffKeys = d && d.before !== undefined && d.after !== undefined ? [["", d.before, d.after]] : d && d.changes ? Object.entries(d.changes).map(([k, v]) => [k, v.before, v.after]) : null;
  const copy = text => {
    navigator.clipboard && navigator.clipboard.writeText(text);
    const t = document.querySelector("paper-toast");
    if (t) t.show("Copied.");
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "al-drawer-backdrop",
    onClick: onClose
  }, /*#__PURE__*/React.createElement("div", {
    className: "al-drawer",
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/React.createElement("div", {
    className: "al-drawer-head"
  }, /*#__PURE__*/React.createElement("b", null, "Activity detail"), /*#__PURE__*/React.createElement("span", {
    className: "material-icons",
    style: {
      cursor: "pointer"
    },
    onClick: onClose
  }, "close")), /*#__PURE__*/React.createElement("table", {
    className: "territory-table al-facts"
  }, /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "fact-label"
  }, "Activity"), /*#__PURE__*/React.createElement("td", null, sentence(row))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "fact-label"
  }, "Action"), /*#__PURE__*/React.createElement("td", {
    className: "code",
    style: {
      display: "table-cell"
    }
  }, row.action)), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "fact-label"
  }, "Actor"), /*#__PURE__*/React.createElement("td", null, row.actorName || row.actorId || "—", " \xB7 ", row.actorType)), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "fact-label"
  }, "Resource"), /*#__PURE__*/React.createElement("td", null, row.resourceType || "—", row.resourceName ? ` · ${row.resourceName}` : "", row.resourceId ? ` (${row.resourceId})` : "")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "fact-label"
  }, "When"), /*#__PURE__*/React.createElement("td", null, w.label, " \xB7 ", w.utc)), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "fact-label"
  }, "Source"), /*#__PURE__*/React.createElement("td", null, row.ipAddress || "—", row.userAgent ? ` · ${String(row.userAgent).slice(0, 60)}` : "")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "fact-label"
  }, "Outcome"), /*#__PURE__*/React.createElement("td", null, row.outcome || "success", " \xB7 ", row.severity || "Info")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "fact-label"
  }, "Correlation"), /*#__PURE__*/React.createElement("td", null, row.correlationId ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "code",
    style: {
      display: "inline"
    }
  }, row.correlationId), " ", /*#__PURE__*/React.createElement("a", {
    style: {
      cursor: "pointer",
      textDecoration: "underline"
    },
    onClick: () => copy(row.correlationId)
  }, "copy")) : "—")), row.rowHash ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "fact-label"
  }, "Row hash"), /*#__PURE__*/React.createElement("td", {
    className: "code",
    style: {
      display: "table-cell"
    }
  }, String(row.rowHash).slice(0, 24), "\u2026")) : null)), diffKeys && /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table",
    style: {
      marginTop: "10px"
    }
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Field"), /*#__PURE__*/React.createElement("th", null, "Before"), /*#__PURE__*/React.createElement("th", null, "After"))), /*#__PURE__*/React.createElement("tbody", null, diffKeys.map(([k, b, a], i) => /*#__PURE__*/React.createElement("tr", {
    key: i
  }, /*#__PURE__*/React.createElement("td", null, k || row.resourceName || "value"), /*#__PURE__*/React.createElement("td", null, String(b)), /*#__PURE__*/React.createElement("td", {
    className: "strong"
  }, String(a)))))), d && /*#__PURE__*/React.createElement("div", {
    className: "al-json"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex",
    style: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "territory-hint",
    style: {
      margin: 0
    }
  }, "Raw details"), /*#__PURE__*/React.createElement("a", {
    style: {
      cursor: "pointer",
      textDecoration: "underline",
      fontSize: "12px"
    },
    onClick: () => copy(JSON.stringify(d, null, 2))
  }, "Copy JSON")), /*#__PURE__*/React.createElement("pre", null, JSON.stringify(d, null, 2)))));
}
export default withRouter(ActivityLogViewComponent);